import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

interface NotificationPreference {
  [key: string]: string | boolean;
}

interface EditableNotificationPreferencesProps {
  userId: string;
  currentPreferences?: NotificationPreference;
  userRole?: string;
}

const EditableNotificationPreferences: React.FC<EditableNotificationPreferencesProps> = ({
  userId,
  currentPreferences = {},
  userRole = 'user'
}) => {
  const [preferences, setPreferences] = useState<NotificationPreference>(currentPreferences);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setPreferences(currentPreferences);
  }, [currentPreferences]);

  // Define notification categories based on user role
  const getNotificationCategories = () => {
    const baseCategories = [
      {
        title: 'System Notifications',
        description: 'Important system updates and announcements',
        items: [
          { key: 'system_announcement', label: 'System Announcements' },
          { key: 'feature_update', label: 'New Feature Updates' }
        ]
      }
    ];

    if (userRole === 'admin' || userRole === 'super_admin') {
      return [
        {
          title: 'Campaign Management',
          description: 'Notifications about campaign submissions and status changes',
          items: [
            { key: 'admin_campaign_submitted', label: 'New Campaign Submissions' },
            { key: 'campaign_running', label: 'Campaigns Started' },
            { key: 'error_alert', label: 'System Error Alerts' }
          ]
        },
        ...baseCategories
      ];
    } else if (userRole === 'csr') {
      return [
        {
          title: 'Campaign Assignments',
          description: 'Notifications about campaigns assigned to you',
          items: [
            { key: 'campaign_submitted', label: 'New Campaigns to Review' },
            { key: 'csr_assigned', label: 'Campaign Assignments' }
          ]
        },
        {
          title: 'Lead Response Management',
          description: 'Notifications about lead responses and replies',
          items: [
            { key: 'reply_requested', label: 'Reply Requests from Users' },
            { key: 'lead_response_new', label: 'New Lead Responses' }
          ]
        },
        ...baseCategories
      ];
    } else {
      // Regular users
      return [
        {
          title: 'Lead Response Notifications',
          description: 'Updates about your lead responses and conversations',
          items: [
            { key: 'lead_response_new', label: 'New Lead Responses' },
            { key: 'lead_response_followup', label: 'Follow-up Messages' },
            { key: 'reply_sent', label: 'Replies Sent by CSR' },
            { key: 'near_message_limit', label: 'Message Limit Warnings' },
            { key: 'conversation_closed', label: 'Conversation Closed' }
          ]
        },
        {
          title: 'Campaign Notifications',
          description: 'Updates about your campaign status',
          items: [
            { key: 'campaign_approved', label: 'Campaign Approved' },
            { key: 'campaign_needs_editing', label: 'Campaign Needs Changes' },
            { key: 'campaign_running', label: 'Campaign Started' }
          ]
        },
        ...baseCategories
      ];
    }
  };

  const frequencyOptions = [
    { value: 'immediate', label: 'Immediately', description: 'Get notified as soon as events happen' },
    { value: 'hourly', label: 'Hourly', description: 'Receive a summary every hour' },
    { value: 'daily', label: 'Daily', description: 'Get a daily digest' },
    { value: 'weekly', label: 'Weekly', description: 'Receive a weekly summary' },
    { value: 'never', label: 'Never', description: 'Turn off this notification' }
  ];

  const handlePreferenceChange = (key: string, value: string | boolean) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const { error } = await supabase
        .from('users')
        .update({ 
          notification_preferences: preferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      setSuccessMessage('Notification preferences updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save preferences');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type: 'system_announcement',
          title: 'Test Notification',
          message: 'This is a test notification to verify your settings are working correctly.',
          data: {
            test: true,
            timestamp: new Date().toISOString()
          },
          priority: 'low',
          created_at: new Date().toISOString()
        });

      if (error) throw error;

      setSuccessMessage('Test notification sent! Check your notification bell.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error sending test notification:', error);
      setErrorMessage('Failed to send test notification');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const categories = getNotificationCategories();

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {errorMessage}
        </div>
      )}

      {/* Email Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Enable Email Notifications</label>
              <p className="text-xs text-gray-500 mt-1">Receive notifications via email in addition to in-app</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.email_enabled as boolean || false}
                onChange={(e) => handlePreferenceChange('email_enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {preferences.email_enabled && (
            <div>
              <label className="text-sm font-medium text-gray-700">Email Frequency</label>
              <select
                value={preferences.email_frequency as string || 'immediate'}
                onChange={(e) => handlePreferenceChange('email_frequency', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="immediate">Send immediately</option>
                <option value="hourly">Hourly digest</option>
                <option value="daily">Daily digest</option>
                <option value="weekly">Weekly digest</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Notification Categories */}
      {categories.map((category, index) => (
        <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.title}</h3>
          <p className="text-sm text-gray-500 mb-4">{category.description}</p>
          
          <div className="space-y-4">
            {category.items.map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <label className="text-sm text-gray-700 flex-1">{item.label}</label>
                <select
                  value={preferences[item.key] as string || 'immediate'}
                  onChange={(e) => handlePreferenceChange(item.key, e.target.value)}
                  className="ml-4 text-sm border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {frequencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Quiet Hours */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quiet Hours</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Enable Quiet Hours</label>
              <p className="text-xs text-gray-500 mt-1">Pause notifications during specific hours</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.quiet_hours_enabled as boolean || false}
                onChange={(e) => handlePreferenceChange('quiet_hours_enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {preferences.quiet_hours_enabled && (
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">Start Time</label>
                <input
                  type="time"
                  value={preferences.quiet_hours_start as string || '22:00'}
                  onChange={(e) => handlePreferenceChange('quiet_hours_start', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">End Time</label>
                <input
                  type="time"
                  value={preferences.quiet_hours_end as string || '08:00'}
                  onChange={(e) => handlePreferenceChange('quiet_hours_end', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handleTestNotification}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Send Test Notification
        </button>
        
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default EditableNotificationPreferences;